import React, { useCallback, useContext, useEffect, useState } from "react";
import { Button, Form, Modal, Table } from "react-bootstrap";
import Layout from "./layout/Layout";
import api from "../../../helper/apiAdmin";
import { getServerURL } from "../../../helper/envConfig";
import { CartContext } from "../../../context/CartContext";

const MarketplaceListings = () => {
  const { setMainLoder } = useContext(CartContext);
  const serverURL = getServerURL();
  const [offers, setOffers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total: 0 });
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", price: "", quantity: "", status: "" });

  const loadOffers = useCallback(async () => {
    setMainLoder(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: "20" });
      if (query.trim()) params.set("q", query.trim());
      if (status) params.set("status", status);
      if (type) params.set("type", type);
      const response = await api.getWithToken(
        `${serverURL}api/storefront/admin/offers?${params.toString()}`
      );
      setOffers(response.data.offers || []);
      setPagination(response.data.pagination || { page: 1, total_pages: 1, total: 0 });
    } catch (error) {
      console.error("Marketplace listings failed to load", error);
      setOffers([]);
    } finally {
      setMainLoder(false);
    }
  }, [page, query, status, type, serverURL, setMainLoder]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const updateOffer = async (offerId, values) => {
    setMainLoder(true);
    try {
      await api.patchWithToken(
        `${serverURL}api/storefront/admin/offers/${offerId}`,
        values
      );
      setEditing(null);
      await loadOffers();
    } catch (error) {
      console.error("Marketplace listing update failed", error);
      window.alert(error?.response?.data?.message || "Could not update this listing.");
    } finally {
      setMainLoder(false);
    }
  };

  const deleteOffer = async (offer) => {
    if (!window.confirm(`Permanently delete “${offer.title}”? This cannot be undone.`)) return;
    setMainLoder(true);
    try {
      await api.deleteWithToken(
        `${serverURL}api/storefront/admin/offers/${offer._id}`
      );
      await loadOffers();
    } catch (error) {
      console.error("Marketplace listing delete failed", error);
      window.alert(error?.response?.data?.message || "Could not delete this listing.");
    } finally {
      setMainLoder(false);
    }
  };

  const openEditor = (offer) => {
    setEditing(offer);
    setForm({
      title: offer.title || "",
      price: offer.pricing?.amount ?? "",
      quantity: offer.inventory?.quantity ?? "",
      status: offer.status || "draft",
    });
  };

  const ownerName = (offer) =>
    offer.owner_id?.name ||
    offer.owner_id?.username ||
    offer.owner_id?.email ||
    "Unknown owner";

  return (
    <Layout>
      <div className="border-green">
        <div className="p-3 p-sm-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
            <div>
              <h3 className="mb-1">Creator Marketplace Listings</h3>
              <div className="text-muted">
                {pagination.total || 0} creator and business listings. Newest first.
              </div>
            </div>
            <Button variant="outline-primary" onClick={loadOffers}>Refresh</Button>
          </div>

          <div className="row g-2 mb-3">
            <div className="col-lg-6">
              <Form.Control
                value={query}
                placeholder="Search listing, category, or location"
                onChange={(event) => {
                  setPage(1);
                  setQuery(event.target.value);
                }}
              />
            </div>
            <div className="col-lg-3">
              <Form.Select value={status} onChange={(event) => {
                setPage(1);
                setStatus(event.target.value);
              }}>
                <option value="">All statuses</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="paused">Paused</option>
                <option value="sold_out">Sold out</option>
              </Form.Select>
            </div>
            <div className="col-lg-3">
              <Form.Select value={type} onChange={(event) => {
                setPage(1);
                setType(event.target.value);
              }}>
                <option value="">All listing types</option>
                <option value="closet_item">Closet items</option>
                <option value="physical_product">Boutique products</option>
                <option value="service">Services</option>
                <option value="restaurant_reservation">Restaurants</option>
                <option value="prepaid_experience">Experiences</option>
                <option value="hotel_recommendation">Hotels</option>
              </Form.Select>
            </div>
          </div>

          <Table responsive bordered hover className="mb-3">
            <thead>
              <tr>
                <th>Photo</th>
                <th>Listing</th>
                <th>Owner</th>
                <th>Type</th>
                <th>Category</th>
                <th>Price</th>
                <th>Stock</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {offers.map((offer) => (
                <tr key={offer._id}>
                  <td>
                    {offer.media?.[0]?.url ? (
                      <img
                        src={offer.media[0].thumbnail_url || offer.media[0].url}
                        alt=""
                        style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8 }}
                      />
                    ) : "—"}
                  </td>
                  <td><strong>{offer.title}</strong></td>
                  <td>
                    <div>{ownerName(offer)}</div>
                    <small className="text-muted">{offer.owner_id?.email}</small>
                  </td>
                  <td>{offer.offer_type?.replaceAll("_", " ")}</td>
                  <td>{offer.category_name || offer.category_id?.name || "—"}</td>
                  <td>
                    {offer.pricing?.currency || "USD"} {offer.pricing?.amount ?? 0}
                  </td>
                  <td>{offer.inventory?.quantity ?? "—"}</td>
                  <td>{offer.status?.replaceAll("_", " ")}</td>
                  <td>{offer.createdAt ? new Date(offer.createdAt).toLocaleString() : "—"}</td>
                  <td>
                    <div className="d-flex flex-column gap-2">
                      <Button size="sm" onClick={() => openEditor(offer)}>Edit</Button>
                      <Button
                        size="sm"
                        variant={offer.status === "published" ? "warning" : "success"}
                        onClick={() => updateOffer(offer._id, {
                          status: offer.status === "published" ? "paused" : "published"
                        })}
                      >
                        {offer.status === "published" ? "Hide" : "Publish"}
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => deleteOffer(offer)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {!offers.length && (
                <tr>
                  <td colSpan="10" className="text-center py-5 text-muted">
                    No marketplace listings match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <div className="d-flex justify-content-between align-items-center">
            <Button
              variant="outline-secondary"
              disabled={page <= 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
            >
              Previous
            </Button>
            <span>Page {pagination.page || page} of {pagination.total_pages || 1}</span>
            <Button
              variant="outline-secondary"
              disabled={page >= (pagination.total_pages || 1)}
              onClick={() => setPage((value) => value + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit marketplace listing</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              value={form.title}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Price</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              value={form.price}
              onChange={(event) => setForm({ ...form, price: event.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={form.quantity}
              onChange={(event) => setForm({ ...form, quantity: event.target.value })}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={form.status}
              onChange={(event) => setForm({ ...form, status: event.target.value })}
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="paused">Paused</option>
              <option value="sold_out">Sold out</option>
            </Form.Select>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            onClick={() => updateOffer(editing._id, {
              title: form.title,
              price: Number(form.price),
              quantity: Number(form.quantity),
              status: form.status,
            })}
          >
            Save changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Layout>
  );
};

export default MarketplaceListings;
